/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, useTheme, Grid } from '@mui/material';
import useStyles from './FileContainer.styles.ts';
import { Typo } from 'components/primitive';
import FileIcon from '@mui/icons-material/Description';
import PhotoIcon from '@mui/icons-material/Photo';
import ClearIcon from '@mui/icons-material/Clear';
import { FileType } from 'interfaces/ia.interface.ts';
import { resolveFileDownloadURL } from '@/utilities/FileUploadAPI.ts';

type FileContainerProps = {
  name: string;
  bytes: string;
  readOnly?: boolean;
  item: FileType;
  downloadFile?: (item: FileType) => void;
  removeFile?: (item: FileType) => void;
  jwtToken: string;
  imagePreview?: boolean;
};

function FileContainer({ readOnly = true, imagePreview = true, ...props }: FileContainerProps) {
  const theme = useTheme();
  const classes = useStyles(theme);
  // The signed link together with the stored path it was resolved for. Documents
  // keys its cards by index, so a card can be handed another file; a link is only
  // shown while it still belongs to this card's file.
  const [thumbnail, setThumbnail] = useState<{ uri: string; url: string } | null>(null);
  // The file this card last resolved a link for successfully. A session
  // renewal changes `jwtToken` on every mounted card at once; without this,
  // every card re-requests its signed link even when it already has one.
  const resolvedForRef = useRef<string | null>(null);

  const handleDownload = () => {
    if (props.downloadFile) props.downloadFile(props.item);
    return null;
  };

  const handleRemove = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (props.removeFile) props.removeFile(props.item);
    return null;
  };
  // Check if the file is an image
  const isImage =
    (props.item &&
      props.item?.cbrnDataFileName &&
      props.item?.cbrnDataFileName?.match(/\.(jpeg|jpg|gif|png)$/) !== null) ||
    (props.item &&
      props.item?.file_name &&
      props.item?.file_name?.match(/\.(jpeg|jpg|gif|png)$/) !== null);
  const fileURI = props?.item?.cbrnDataFileURIID;

  //   happy:          https link resolves -> the <img> preview shows it
  //   nil/null:       no stored path, not an image, or preview off -> no request, photo icon only
  //   empty/zero:     no usable link in the response -> photo icon only
  //   upstream error: logged in resolveFileDownloadURL -> photo icon only
  useEffect(() => {
    if (!isImage || !imagePreview || !fileURI) return;
    // Already have a link for this file -- a jwtToken change alone (a session
    // renewal) is not a reason to re-request it. A card with no link yet still
    // retries, so a renewal can recover a card that failed on a stale token.
    if (resolvedForRef.current === fileURI) return;
    let isCurrent = true;
    resolveFileDownloadURL(fileURI, props.jwtToken).then((url) => {
      // Dropped when the card unmounted, or its file or token changed, meanwhile.
      if (!isCurrent) return;
      if (url) {
        resolvedForRef.current = fileURI;
        setThumbnail({ uri: fileURI, url });
      } else {
        setThumbnail(null);
      }
    });
    return () => {
      isCurrent = false;
    };
  }, [fileURI, props.jwtToken, isImage, imagePreview]);

  const imageURL = thumbnail && thumbnail.uri === fileURI ? thumbnail.url : null;

  return (
    <Grid item xs={12} sm={6} md={3} margin={0}>
      <Box sx={classes.wrap}>
        <Box sx={classes.file} onClick={props.downloadFile ? handleDownload : undefined}>
          <Box>
            {props?.item?.downloadLink || isImage ? (
              <PhotoIcon sx={{ width: '24px', color: theme.palette.grey[500] }} />
            ) : (
              <FileIcon sx={{ width: '24px', color: theme.palette.grey[500] }} />
            )}
          </Box>
          <Box sx={classes.fileNameWrapper}>
            <Typo sx={classes.fileName}>{props.name}</Typo>
            <Typo sx={classes.fileSize}>{props.bytes}</Typo>
          </Box>

          <Box sx={classes.removeBtn} hidden={readOnly}>
            <IconButton
              aria-label='Delete'
              aria-haspopup='true'
              onClick={props.removeFile ? handleRemove : undefined}>
              <ClearIcon className='icon-small' />
            </IconButton>
          </Box>
        </Box>
        {isImage && imageURL && imagePreview ? (
          <Box sx={{ paddingTop: '12px' }}>
            <img
              src={imageURL}
              alt={props.name}
              style={{ maxWidth: '100%' }}
              onError={() => setThumbnail(null)}
            />
          </Box>
        ) : props?.item?.downloadLink ? (
          <Box sx={{ paddingTop: '12px' }}>
            <img alt='photoprev' src={props.item.downloadLink} style={{ maxWidth: '100%' }} />
          </Box>
        ) : isImage && imagePreview ? (
          // Reserves the preview's height while the signed link resolves, so
          // the card does not grow after the rest of the list has painted.
          <Box sx={{ paddingTop: '12px' }}>
            <Box sx={{ width: '100%', height: 80 }} />
          </Box>
        ) : null}
        {isImage && !imagePreview ? (
          //preview not available offline
          <Box>
            <Typo>Preview not available offline</Typo>
          </Box>
        ) : null}
      </Box>
    </Grid>
  );
}

export default FileContainer;
